import { isSameDay } from 'date-fns/isSameDay';
import React, { useMemo, useState } from 'react';

import { format, getZonedDate, ZonedDate } from '@eika-infrastruktur/date';
import { Button, RowButton } from '@eika/button';
import { Calendar, getDateDisplayValue } from '@eika/calendar';
import { Icon, IconTypes } from '@eika/icon';
import { WarningLabel } from '@eika/label';
import { ModalDialogType } from '@eika/modal';

import { useBankingHolidays } from '../../../fetchHooks/useBankingDays';
import type { PaymentInterval } from '../../../generated-types';
import useEscape from '../../../hooks/useEscapeKey';
import useTranslation, { format as formatTranslate } from '../../../hooks/useTranslation';
import TranslationsConfigType from '../../../translations/translationType';
import { EikaModal } from '../../EikaModal';
import { StickyFooterRow } from '../StickyFooter';
import EndDateField from './EndDateField';
import PaymentIntervalField from './PaymentIntervalField';
import { clonedZonedDateToDate } from './utils';

import './DateField.scss';

interface NotificationSelectedData {
    id: string;
    text: string;
}

interface Notification {
    selectedDay?: NotificationSelectedData[];
    selectedDate?: NotificationSelectedData[];
    timezoneDifferentMessage?: string;
}

export const getNotifications = (
    translated: TranslationsConfigType,
    onlyBusinessDays: boolean,
    bankHolidayDates: ZonedDate[] | undefined,
): Notification => {
    if (onlyBusinessDays) {
        if (!bankHolidayDates) return {};
        return {
            selectedDay: [
                {
                    id: '6',
                    text: translated.dateField.bankHolidayMessage,
                },
                {
                    id: '0',
                    text: translated.dateField.bankHolidayMessage,
                },
            ],
            selectedDate: bankHolidayDates.map((day) => ({
                id: format(day, 'dd.MM'),
                text: translated.dateField.bankHolidayMessage,
            })),
            timezoneDifferentMessage: translated.dateField.diffTimeZoneMessage,
        };
    }
    return { timezoneDifferentMessage: translated.dateField.diffTimeZoneMessage };
};

interface Props {
    label: string;
    minDate?: Date;
    maxDate?: Date;
    date?: Date;
    onChange: (date?: Date) => void;
    onChangePaymentInterval?: (interval?: PaymentInterval) => void;
    onShow?: () => void;
    warning?: string;
    locked?: boolean;
    disabled?: boolean;
    onlyBusinessDays?: boolean;
    paymentInterval?: PaymentInterval;
    paymentAgreement?: boolean;
    recurringTransfer?: boolean;
    immediate?: boolean;
    endDate?: ZonedDate;
    onSetEndDate?: (date?: Date) => void;
    animateModalAs?: ModalDialogType;
}

const today = getZonedDate();

const DateField: React.FC<Props> = ({
    label,
    minDate,
    maxDate,
    date,
    onChange,
    onShow,
    locked,
    warning,
    disabled = false,
    onlyBusinessDays = false,
    paymentInterval,
    paymentAgreement,
    onChangePaymentInterval,
    recurringTransfer = false,
    immediate = false,
    endDate,
    onSetEndDate,
    animateModalAs,
}) => {
    const translated = useTranslation();
    const [showPaymentModal, setShowPaymentModal] = useState<boolean>(false);
    const [showEndDate, setShowEndDate] = useState(false);
    const [showPicker, setShowPicker] = useState<boolean>(false);
    const [spanDates, setSpanDates] = useState<ZonedDate[]>([getZonedDate(), getZonedDate()]);
    const { data: bankHolidayDates } = useBankingHolidays(spanDates[0], spanDates[1], {
        select: (days: ZonedDate[]) => days,
    });
    const notification = getNotifications(translated, onlyBusinessDays, bankHolidayDates);

    useEscape(() => setShowPicker(false));

    const handleCalendarSelect = (zonedDate?: ZonedDate) => {
        const dateClone = clonedZonedDateToDate(zonedDate);
        onChange(dateClone);
    };

    const triggerShow = () => {
        setShowPicker(true);
        onShow?.();
    };

    const selectToday = () => onChange(minDate ?? today);

    const dateMemoized = useMemo(() => (date ? getZonedDate(date) : undefined), [date]);
    const minDateMemoized = useMemo(() => (minDate ? getZonedDate(minDate) : undefined), [minDate]);
    const maxDateMemoized = useMemo(() => (maxDate ? getZonedDate(maxDate) : undefined), [maxDate]);

    return (
        <>
            <RowButton
                onClick={() => triggerShow()}
                className="date-field__button"
                disabled={disabled || locked || immediate}
                data-testid="date-field"
            >
                <div className="date-field__layout">
                    <div className="date-field__label">
                        <span className="date-field__label">{label}</span>
                        <span className="date-field__value">
                            {immediate ? translated.immediatePayment.title : getDateDisplayValue(date)}
                        </span>
                        <span className="date-field__payment-interval">
                            {paymentInterval && translated.dateField.recurringPaymentDateInterval[paymentInterval]}
                        </span>
                        {endDate && (
                            <span className="date-field__payment-interval">
                                {formatTranslate(
                                    translated.dateField.paymentInterval.withEndDate,
                                    getDateDisplayValue(endDate),
                                )}
                            </span>
                        )}
                    </div>
                    <div className="date-field__icon">
                        {locked || immediate ? (
                            <Icon type={IconTypes.PIN_KODE_24} className="icon--24" ariaLabel="Låst" />
                        ) : (
                            <Icon type={IconTypes.LITEN_PIL_HOEYRE} className="icon--20" ariaLabel="Endre" />
                        )}
                    </div>
                </div>
                {!!warning && <WarningLabel text={warning} className="date-field__warning" />}
            </RowButton>
            <EikaModal
                className="date-field__modal"
                id="dateinput"
                open={showPicker}
                setOpen={setShowPicker}
                animateAs={animateModalAs}
            >
                <h2 className="date-field__heading">{translated.dateField.title}</h2>
                <div className="date-field__datepicker-container">
                    <Calendar
                        selected={dateMemoized}
                        minDate={minDateMemoized}
                        maxDate={maxDateMemoized}
                        onSelect={handleCalendarSelect}
                        notification={notification}
                        setSpanDates={setSpanDates}
                        bankHolidayDates={bankHolidayDates}
                    />
                    {date && !isSameDay(date, minDate ?? today) && (
                        <Button
                            className="date-field__datepicker-today-button"
                            buttonType="negative"
                            onClick={selectToday}
                        >
                            {translated.dateField.today}
                        </Button>
                    )}
                </div>
                {paymentAgreement && (
                    <>
                        <div className="date-field__list">
                            <RowButton onClick={() => setShowPaymentModal(true)}>
                                <RowButton.AccountSelectorLayout
                                    accountAlias={translated.dateField.paymentInterval.alias}
                                    amount={
                                        paymentInterval
                                            ? translated.dateField.recurringPaymentInterval[paymentInterval]
                                            : ''
                                    }
                                    icon={IconTypes.LITEN_PIL_HOEYRE}
                                />
                            </RowButton>
                            {onSetEndDate && (
                                <RowButton onClick={() => setShowEndDate(true)}>
                                    <RowButton.AccountSelectorLayout
                                        accountAlias={translated.dateField.paymentInterval.lastPaymentDate}
                                        amount={
                                            endDate
                                                ? getDateDisplayValue(endDate)
                                                : translated.dateField.paymentInterval.noLastPaymentDate
                                        }
                                        icon={IconTypes.LITEN_PIL_HOEYRE}
                                    />
                                </RowButton>
                            )}
                        </div>
                    </>
                )}
                <StickyFooterRow>
                    <Button size="small" onClick={() => setShowPicker(false)}>
                        {translated.continue}
                    </Button>
                </StickyFooterRow>
            </EikaModal>
            {onSetEndDate && (
                <EndDateField
                    date={endDate}
                    onChange={onSetEndDate}
                    minDate={date}
                    visible={showEndDate}
                    setVisible={setShowEndDate}
                />
            )}
            <PaymentIntervalField
                onChange={(value) => onChangePaymentInterval?.(value)}
                paymentInterval={paymentInterval}
                recurringTransfer={recurringTransfer}
                visible={showPaymentModal}
                setOpen={setShowPaymentModal}
            />
        </>
    );
};

export default DateField;
